import { useState, useEffect } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-white border-b border-background-100'
        }`}
      >
        <div className="w-full px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-18">
            <a href="/" className="flex items-center gap-2.5 cursor-pointer flex-shrink-0">
              <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-white">
                <i className="ri-shopping-bag-3-line text-base"></i>
              </span>
              <span className="font-heading font-semibold text-foreground-900 text-sm md:text-base whitespace-nowrap">
                Basetao Sheet Hub
              </span>
            </a>

            <div className="hidden md:flex items-center gap-6">
              <a
                href="/"
                className="text-sm font-label font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                Home
              </a>
              <a
                href="/basetao-spreadsheet-guide"
                className="text-sm font-label font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                Shopping Guide
              </a>
              <a
                href="/basetao-review"
                className="text-sm font-label font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                Review
              </a>
              <div className="relative group">
                <button className="flex items-center gap-1.5 text-sm font-label font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap">
                  Categories
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </button>
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="bg-white border border-background-200/70 rounded-xl overflow-hidden w-56 max-h-[320px] overflow-y-auto">
                    {categories.map((cat, idx) => (
                      <a
                        key={idx}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-600 hover:bg-background-100 hover:text-primary-600 transition-colors duration-150 cursor-pointer border-b border-background-100 last:border-b-0"
                      >
                        <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100">
                          <i className={`${cat.icon} text-primary-500 text-xs`}></i>
                        </span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap px-5 py-2 rounded-full bg-primary-500 text-white text-sm font-label font-semibold hover:bg-primary-600 transition-all duration-300 cursor-pointer"
              >
                Shop Now
              </a>
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-[99] md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute top-16 left-0 right-0 bg-white border-b border-background-200/70 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="px-4 py-4 space-y-1">
              <a href="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-700 font-label font-medium hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-home-4-line text-lg text-primary-500"></i>
                Home
              </a>
              <a href="/basetao-spreadsheet-guide" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-700 font-label font-medium hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-book-open-line text-lg text-primary-500"></i>
                Shopping Guide
              </a>
              <a href="/basetao-review" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-700 font-label font-medium hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-star-line text-lg text-primary-500"></i>
                Review
              </a>
              <div className="border-t border-background-100 my-2"></div>
              <p className="px-4 py-2 text-xs font-label font-semibold uppercase tracking-wider text-foreground-400">Categories</p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat, idx) => (
                  <a
                    key={idx}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs text-foreground-600 font-label hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <span className="w-6 h-6 flex items-center justify-center rounded-md bg-background-100">
                      <i className={`${cat.icon} text-primary-500 text-xs`}></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
              <div className="border-t border-background-100 my-2"></div>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary-500 text-white font-label font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-shopping-bag-3-line"></i>
                Shop Now
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}